// CookieConsent.jsx - GDPR/CCPA Compliant Cookie Banner
import React, { useState, useEffect } from 'react';
import './cookieconsent.css';

const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, can't be changed
        analytics: false,
        marketing: false,
        functional: true
    });

    useEffect(() => {
        // Check if user has already consented
        const consent = localStorage.getItem('cookieConsent');
        const consentDate = localStorage.getItem('cookieConsentDate');

        if (!consent) {
            // Show banner after 2 seconds for better UX
            setTimeout(() => setShowBanner(true), 2000);
        } else {
            // Check if consent is older than 1 year (re-consent required)
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            if (consentDate && new Date(consentDate) < oneYearAgo) {
                setTimeout(() => setShowBanner(true), 2000);
            }
        }
    }, []);

    const handleAcceptAll = () => {
        const fullConsent = {
            necessary: true,
            analytics: true,
            marketing: true,
            functional: true
        };

        saveConsent(fullConsent);
        setShowBanner(false);

        // Initialize analytics/marketing scripts
        loadTrackingScripts();
    };

    const handleAcceptSelected = () => {
        saveConsent(preferences);
        setShowBanner(false);

        // Only load selected scripts
        if (preferences.analytics) {
            loadAnalytics();
        }
        if (preferences.marketing) {
            loadMarketing();
        }
    };

    const handleRejectAll = () => {
        const minimalConsent = {
            necessary: true,
            analytics: false,
            marketing: false,
            functional: false
        };

        saveConsent(minimalConsent);
        setShowBanner(false);

        // Disable all non-essential cookies
        disableTracking();
    };

    const saveConsent = (consent) => {
        localStorage.setItem('cookieConsent', JSON.stringify(consent));
        localStorage.setItem('cookieConsentDate', new Date().toISOString());
        localStorage.setItem('cookieConsentIP', 'pending'); // Get IP from backend

        // Send to backend for legal compliance record
        fetch('/api/privacy/consent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                consent,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                // IP will be captured server-side
            })
        }).catch(() => { /* non-blocking */ });
    };

    const loadTrackingScripts = () => {
        // Google Analytics
        loadAnalytics();
        // Marketing/Ads
        loadMarketing();
    };

    const loadAnalytics = () => {
        // Only load if consented
        if (window.location.hostname !== 'localhost') {
            // Google Analytics 4
            const script = document.createElement('script');
            script.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX';
            script.async = true;
            document.head.appendChild(script);

            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-XXXXXXXXXX', {
                anonymize_ip: true, // GDPR compliance
                cookie_flags: 'SameSite=Strict;Secure'
            });
        }
    };

    const loadMarketing = () => {
        // Marketing pixels (Facebook, LinkedIn, etc.)
        console.log('Marketing cookies enabled');
    };

    const disableTracking = () => {
        // Remove all tracking cookies
        document.cookie.split(";").forEach(function(c) {
            document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });

        // Disable Google Analytics
        window['ga-disable-G-XXXXXXXXXX'] = true;
    };

    if (!showBanner) return null;

    return (
        <>
            {/* Cookie Banner */}
            <div className="cookie-consent-banner">
                <div className="cookie-consent-container">
                    <div className="cookie-content">
                        <div className="cookie-header">
                            <h3>🍪 Cookie Consent</h3>
                            <p className="cookie-title">Your Privacy Matters</p>
                        </div>

                        <p className="cookie-description">
                            We use cookies for analytics and security — we <strong>never</strong> sell your data.
                        </p>

                        <div className="cookie-warning">
                            <i className="uil uil-shield-check"></i>
                            <span>
                                IPs &amp; device info are logged for security; abuse is reported.
                            </span>
                        </div>

                        {showDetails && (
                            <div className="cookie-details">
                                <h4>Cookie Categories:</h4>

                                <div className="cookie-category">
                                    <label className="cookie-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.necessary}
                                            disabled
                                        />
                                        <span className="slider"></span>
                                        <div className="category-info">
                                            <strong>Essential Cookies</strong> (Always Active)
                                            <p>Required for website functionality, security, and fraud prevention.</p>
                                            <small>Includes: Session cookies, CSRF tokens, authentication</small>
                                        </div>
                                    </label>
                                </div>

                                <div className="cookie-category">
                                    <label className="cookie-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.functional}
                                            onChange={(e) => setPreferences({
                                                ...preferences,
                                                functional: e.target.checked
                                            })}
                                        />
                                        <span className="slider"></span>
                                        <div className="category-info">
                                            <strong>Functional Cookies</strong>
                                            <p>Remembers your preferences (theme, language, etc.)</p>
                                            <small>Includes: Dark mode preference, chat history</small>
                                        </div>
                                    </label>
                                </div>

                                <div className="cookie-category">
                                    <label className="cookie-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.analytics}
                                            onChange={(e) => setPreferences({
                                                ...preferences,
                                                analytics: e.target.checked
                                            })}
                                        />
                                        <span className="slider"></span>
                                        <div className="category-info">
                                            <strong>Analytics Cookies</strong>
                                            <p>Helps us understand how visitors interact with our website.</p>
                                            <small>Includes: Google Analytics, Hotjar</small>
                                        </div>
                                    </label>
                                </div>

                                <div className="cookie-category">
                                    <label className="cookie-switch">
                                        <input
                                            type="checkbox"
                                            checked={preferences.marketing}
                                            onChange={(e) => setPreferences({
                                                ...preferences,
                                                marketing: e.target.checked
                                            })}
                                        />
                                        <span className="slider"></span>
                                        <div className="category-info">
                                            <strong>Marketing Cookies</strong>
                                            <p>Used to track visitors across websites for relevant ads.</p>
                                            <small>Includes: Facebook Pixel, LinkedIn Insight</small>
                                        </div>
                                    </label>
                                </div>

                                <div className="cookie-data-collection">
                                    <h4>What We Collect & Why:</h4>
                                    <ul>
                                        <li><strong>IP Address:</strong> Security, fraud prevention, geographic analytics</li>
                                        <li><strong>Device Info:</strong> Optimize experience, detect bots</li>
                                        <li><strong>Pages Visited:</strong> Improve content and user experience</li>
                                        <li><strong>Chat Messages:</strong> Provide support, prevent abuse</li>
                                        <li><strong>Contact Info:</strong> Only when you provide it voluntarily</li>
                                    </ul>
                                </div>

                                <div className="cookie-legal">
                                    <p><strong>Your Rights:</strong></p>
                                    <ul>
                                        <li>Access your data anytime</li>
                                        <li>Request deletion (except legal requirements)</li>
                                        <li>Opt-out of marketing</li>
                                        <li>File a complaint with supervisory authority</li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        <div className="cookie-actions">
                            <button
                                className="cookie-settings"
                                onClick={() => setShowDetails(!showDetails)}
                            >
                                {showDetails ? '− Hide' : '+ Customize'} Settings
                            </button>

                            <div className="cookie-buttons">
                                <button
                                    className="cookie-reject"
                                    onClick={handleRejectAll}
                                >
                                    Reject All
                                </button>

                                {showDetails && (
                                    <button
                                        className="cookie-accept-selected"
                                        onClick={handleAcceptSelected}
                                    >
                                        Accept Selected
                                    </button>
                                )}

                                <button
                                    className="cookie-accept"
                                    onClick={handleAcceptAll}
                                >
                                    Accept All
                                </button>
                            </div>
                        </div>

                        <div className="cookie-links">
                            <a href="/privacy-policy" target="_blank">Privacy Policy</a>
                            <span>•</span>
                            <a href="/terms-of-service" target="_blank">Terms of Service</a>
                            <span>•</span>
                            <a href="/acceptable-use" target="_blank">Acceptable Use Policy</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Privacy Badge */}
            <div className="privacy-badge">
                <i className="uil uil-lock"></i>
                <span>GDPR Compliant</span>
            </div>
        </>
    );
};

export default CookieConsent;